import {
  AlertColor,
  Box,
  CircularProgress,
  Grid,
  Typography,
} from '@mui/material';
import SelectableCardGroup from '../../../../SelectableCardGroup';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Control,
  Controller,
  useController,
  UseFormSetValue,
  useFormState,
  useWatch,
} from 'react-hook-form';
import { useQuery } from 'react-query';
import { ProductFamily, ProductModel } from '../../../../../../../../types/api';
import {
  CO_UTILITIES,
  IL_UTILITIES,
  MA_TERRITORIES,
  UNITED_STATES,
} from '../../../../../constants';
import { useAuth } from '../../../../../hooks';
import { api } from '../../../../../services/apis';
import { getRates } from '../../../../../services/apis/InstallerAPIs';
import {
  getDSCRMessage,
  isSpecialState,
  isStateWithREC,
  Message,
} from '../../../../../utils/helpers';
import { Tracker } from '../../../../../utils/tracker';
import {
  FEATURE_FLAGS,
  isFeatureEnabled,
} from '../../../../account/userSettings/DebugForm';
import InputField from '../../../../hook_form_fields/InputField';
import NumberField from '../../../../hook_form_fields/NumberField';
import SelectField from '../../../../hook_form_fields/SelectField';
import AlertMessage from '../../../../pricing-calculator/AlertMessage';
import NewBorrowerFormFieldset from '../NewBorrowerFormFieldset';
import { InitialValues, timelineEstimations } from '../schema';

type MountingKey = 'roof' | 'carport' | 'ground' | 'other';

const MOUNTING_OPTIONS: ReadonlyArray<{ key: MountingKey; label: string }> = [
  { key: 'roof', label: 'Roof Mount' },
  { key: 'carport', label: 'Carport' },
  { key: 'ground', label: 'Ground Mount' },
  { key: 'other', label: 'Other' },
];

export const year1SolarSavingsText =
  'Use a design and proposal software like Energy Toolbase to accurately calculate Year 1 Total Solar Savings.';

const SystemMountingOtherValueInput: FC<{
  control: Control<InitialValues>;
}> = ({ control }) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const mountingOther = useWatch({
    control,
    name: 'mounting.other',
  });
  const {
    field: { onChange },
  } = useController({
    control,
    name: 'mounting.otherValue',
  });
  useEffect(() => {
    if (mountingOther && inputRef.current) {
      inputRef.current.focus();
    }
    onChange(''); // reset the value to empty string when it's changed
  }, [mountingOther, onChange]);
  return (
    <InputField
      control={control}
      name="mounting.otherValue"
      ref={inputRef}
      label=""
      data-testid="otherValue"
      id="mounting.otherValue"
      touched={true}
      isDisabled={!mountingOther}
      placeholder="Describe the mounting hardware"
      multiline
      minRows={1}
      maxRows={4}
      maxLength={140}
      sx={{ paddingTop: '6px', position: 'relative' }}
    />
  );
};

const showSolarEconCalculator = isFeatureEnabled(
  FEATURE_FLAGS.showSolarEconCalculator
);

const SolarProjectInformation: FC<{
  control: Control<InitialValues>;
  setValue: UseFormSetValue<InitialValues>;
  installerHasFlex?: boolean;
}> = ({ control, setValue, installerHasFlex = false }) => {
  const { touchedFields } = useFormState({ control });

  // e.g. '1020' -> 10
  const loanTerm = parseInt(control._formValues.loanTerm?.slice(0, 2));

  const propertyState = useWatch({
    control,
    name: 'application.sys_prop_address_state',
  });
  const mountingValues = useWatch({ control, name: 'mounting' });

  const previousPropertyState = useRef({ propertyState });
  // Reset the rec_info value when the property state changes
  useEffect(() => {
    if (previousPropertyState.current.propertyState !== propertyState) {
      setValue('application.rec_info', null, {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      });
    }
    previousPropertyState.current = { propertyState };
  }, [propertyState, setValue]);

  const [
    loanAmount,
    y1saving,
    y1production,
    systemLocation,
    systemSize,
    recInfo,
    isSpecial,
    loanProductId,
    specialPaymentPercentage,
  ] = useWatch({
    control,
    name: [
      'application.loan_amount',
      'application.year_1_total_solar_savings',
      'application.year_1_system_production_kwh',
      'application.sys_prop_address_state',
      'application.system_size_in_k_ws',
      'application.rec_info',
      'application.is_special',
      'application.loan_product',
      'application.special_payment_percentage',
    ],
  });
  const { account } = useAuth();

  const [interestRate, setInterestRate] = useState(0);
  const [productId, setProductId] = useState('');

  const tracker = Tracker.getInstance();

  const { data } = useQuery('rates', () => getRates());

  const currentRates = useMemo(() => {
    if (!data) return [];
    const { current } = data;
    const standard = current[ProductFamily.STANDARD] ?? [];
    const specialPayment = current[ProductFamily.SPECIAL_PAYMENT] ?? [];

    return [...standard, ...specialPayment];
  }, [data]);

  const currentProducts: ProductModel[] = currentRates.map((rate) => ({
    id: rate.product ? rate.product : '',
    amortizationYears: rate.amortization_years
      ? parseInt(rate.amortization_years)
      : 0,
    dealerFee: rate.dealer_fee,
    loanTermYears: parseInt(rate.loan_term_years),
    productFamily: rate.product_family,
    rateId: rate.id,
    rate: rate.interest_rate,
    rateScenario: rate.pricing_grouping,
    type: 'current',
  }));

  useEffect(() => {
    const product = currentProducts.find((p) => p.id === loanProductId);
    setInterestRate(product?.rate ?? 0);
    setProductId(product?.id ?? '');
  }, [currentProducts, interestRate, isSpecial, loanAmount]);

  const [showLoading, setShowLoading] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [alertContent, setAlertContent] = useState<Message>({
    title: '',
    main: '',
  });
  const [alertType, setAlertType] = useState('warning' as AlertColor);

  let timerId;
  const prevTimerId = useRef(timerId);
  const sendSolarRequest = useCallback(() => {
    return () => {
      clearTimeout(prevTimerId.current);
      prevTimerId.current = setTimeout(() => {
        setShowLoading(true);
        setShowAlert(false);
        api()
          .get(
            `solar-economics?amount=${loanAmount}&productId=${productId}&y1saving=${y1saving}&systemLocation=${systemLocation}` +
              (y1production ? `&y1production=${y1production}` : '') +
              (systemSize ? `&systemSize=${systemSize}` : '') +
              (recInfo ? `&recInfo=${recInfo}` : '') +
              (specialPaymentPercentage
                ? `&specialPaymentPercentage=${specialPaymentPercentage}`
                : '')
          )
          .then((result) => {
            const data = JSON.parse(result.data.join(''));

            setShowLoading(false);
            setShowAlert(true);

            const [alertType, message] = getDSCRMessage(
              data.solarDSCR,
              loanTerm
            );
            setAlertType(alertType);
            setAlertContent({ ...message });
            tracker.track(Tracker.EVENTS.NEW_APPLICANT_SOLAR_CALC_SUCCESS, {
              result: ['error', 'warning'].includes(alertType)
                ? 'recommendation-1'
                : alertType,
              state: systemLocation,
            });
          })
          .catch((err) => {
            const data = err?.response?.data;
            setShowLoading(false);
            setAlertContent({
              ...alertContent,
              title: 'Error',
              main:
                data.message ??
                'Error occurred while attempting to calculate the solar economics',
            });
            setAlertType('error');
            setShowAlert(true);
            tracker.track(Tracker.EVENTS.NEW_APPLICANT_SOLAR_CALC_ERROR, {
              result: alertType,
              state: systemLocation,
            });
          });
      }, 500);
    };
  }, [
    account,
    productId,
    loanAmount,
    recInfo,
    systemLocation,
    systemSize,
    y1production,
    y1saving,
    specialPaymentPercentage,
  ]);

  const previousStateInfo = useRef({
    systemLocation,
    y1production,
    systemSize,
    recInfo,
  });

  useEffect(() => {
    if (!showSolarEconCalculator) return;
    setAlertContent({
      title: '',
      main: '',
    });
    setShowAlert(false);
    if (
      loanAmount &&
      Number(loanAmount) >= 0 &&
      interestRate &&
      interestRate >= 0 &&
      systemLocation &&
      y1saving &&
      Number(y1saving) >= 0
    ) {
      // If recInfo changes, assign it to the previous value
      if (previousStateInfo.current.recInfo !== recInfo) {
        previousStateInfo.current.recInfo = recInfo;
      }

      if (isStateWithREC(systemLocation) && (!y1production || !systemSize)) {
        return;
      }

      if (isSpecialState(systemLocation) && !recInfo) {
        return;
      }
      setShowLoading(true);
      // With a short delay send the request
      sendSolarRequest()();
    }
  }, [
    loanAmount,
    interestRate,
    y1production,
    y1saving,
    systemLocation,
    systemSize,
    recInfo,
    sendSolarRequest,
    setValue,
  ]);

  return (
    <NewBorrowerFormFieldset title="Solar Project Information">
      <Grid container maxWidth="md" spacing={2}>
        {!installerHasFlex && (
          <Grid item xs={12} sm={12}>
            <SelectField
              fullWidth
              control={control}
              name="application.sys_prop_address_state"
              label="Property's State where the system will be installed"
              required
              choices={UNITED_STATES}
            />
          </Grid>
        )}
        {['Colorado', 'Illinois', 'Massachusetts'].includes(propertyState) && (
          <Grid item xs={12} sm={12}>
            <SelectField
              fullWidth
              control={control}
              name="application.rec_info"
              label={
                propertyState == 'Massachusetts'
                  ? 'Utility Territory'
                  : 'Utility Company'
              }
              required={['Colorado', 'Illinois', 'Massachusetts'].includes(
                propertyState
              )}
              choices={
                propertyState == 'Colorado'
                  ? CO_UTILITIES
                  : propertyState == 'Illinois'
                  ? IL_UTILITIES
                  : MA_TERRITORIES
              }
              tooltip={
                propertyState == 'Massachusetts'
                  ? 'The utility territory is based on the area of the state where the solar system resides.'
                  : 'The utility company that will be used to power the solar system.'
              }
            />
          </Grid>
        )}
        {!installerHasFlex && (
          <Grid item xs={12} sm={12}>
            <SelectField
              fullWidth
              control={control}
              name="application.project_timeline_estimation"
              label="Project Timeline Estimation"
              required
              choices={timelineEstimations}
              tooltip="Please indicate your best estimate at this time, it is ok if this information changes later."
            />
          </Grid>
        )}
        <Grid item xs={12} md={6}>
          <NumberField
            thousandSeparator
            decimalScale={3}
            control={control}
            name="application.system_size_in_k_ws"
            data-testid="system_size_in_k_ws"
            label="System Size in kWs"
            required
            fullWidth
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <NumberField
            thousandSeparator
            decimalScale={3}
            control={control}
            name="application.year_1_system_production_kwh"
            data-testid="year_1_system_production_kwh"
            label="Year 1 Solar System Production (kWh)"
            tooltip="The year 1 solar system production in kilowatt-hours"
            required
            fullWidth
          />
        </Grid>
        <Grid item xs={12} sm={12}>
          <NumberField
            thousandSeparator
            decimalScale={2}
            startAdornmentText="$"
            control={control}
            name="application.year_1_total_solar_savings"
            data-testid="year_1_total_solar_savings"
            label="Total Year 1 Utility Bill Savings"
            tooltip="Enter the total year 1 utility bill savings you expect the customer to realize, plus any year 1 revenue from sale of power that the system is expected to generate. Exclude solar incentives such as RECs, depreciation benefits, the Investment Tax Credit, and other one-time grants or tax breaks. Sunstone will add these based on the state. Only include demand charge savings in the utility bill savings if calculated using time-interval data from tools like Energy Toolbase."
            additionalInfo={year1SolarSavingsText}
            required
            fullWidth
          />
        </Grid>
        {showSolarEconCalculator && (
          <Grid item xs={12} sm={12}>
            <Box
              sx={{ maxWidth: '45px', margin: 'auto' }}
              display={showLoading ? 'block' : 'none'}
            >
              <CircularProgress />
            </Box>
            <Box display={showAlert ? 'block' : 'none'}>
              <AlertMessage
                severity={alertType}
                message={alertContent}
                installerName={account?.business_name}
              />
            </Box>
          </Grid>
        )}
        <Grid item xs={12} sm={12}>
          <Typography variant="body2" mb={1}>
            System Mounting Hardware
            <Typography variant="body2" color="error" component="span">
              {' '}
              *
            </Typography>
          </Typography>
          <Controller
            control={control}
            name="mountingChecked"
            render={({ fieldState }) => {
              const { error } = fieldState;
              const mountingTouched = touchedFields['mounting'];
              const anyMountingCheckboxTouched =
                mountingTouched?.['roof'] ||
                mountingTouched?.['carport'] ||
                mountingTouched?.['ground'] ||
                mountingTouched?.['other'];

              return error && anyMountingCheckboxTouched ? (
                <Typography
                  variant="body2"
                  color="error"
                  component="div"
                  mb={1}
                >
                  {error.message}
                </Typography>
              ) : (
                <></>
              );
            }}
          />
          <SelectableCardGroup
            columns={2}
            options={MOUNTING_OPTIONS}
            isSelected={(key) => !!mountingValues?.[key]}
            onToggle={(key) =>
              setValue(`mounting.${key}` as const, !mountingValues?.[key], {
                shouldValidate: true,
                shouldDirty: true,
                shouldTouch: true,
              })
            }
            renderExpanded={(opt) =>
              opt.key === 'other' ? (
                <SystemMountingOtherValueInput control={control} />
              ) : null
            }
          />
        </Grid>
      </Grid>
    </NewBorrowerFormFieldset>
  );
};

export default SolarProjectInformation;
